/**
 * Subtitle Processing Utilities
 *
 * Client-side subtitle processing for VTT/SRT conversion, validation, and normalization.
 * Mirrors the logic from scripts/process-subtitles-admin.mjs for browser usage.
 *
 * ## Supported WebVTT Formatting Tags
 *
 * The following HTML-like tags are valid in WebVTT and will be preserved:
 *
 * - **`<i>text</i>`** - Italic (most common, used for foreign languages, thoughts, emphasis)
 * - **`<b>text</b>`** - Bold (strong emphasis)
 * - **`<u>text</u>`** - Underline (less common, for emphasis)
 * - **`<c>text</c>`** - Class annotation (can style with CSS: `<c.classname>text</c>`)
 * - **`<v Speaker>`** - Voice/speaker annotation (e.g., `<v John>Hello!`)
 * - **`<lang en>text</lang>`** - Language annotation for multilingual content
 * - **`<ruby>text</ruby>`** - Ruby annotation for East Asian text (with `<rt>` for pronunciation)
 * - **`<rt>text</rt>`** - Ruby text (pronunciation guide, used within `<ruby>`)
 *
 * ## Common Usage Examples
 *
 * ```
 * <i>Italic for emphasis or foreign words</i>
 * <b>Bold for strong emphasis</b>
 * <c.speaker-name>Text styled with CSS class</c>
 * <v Alice>Character name followed by dialogue
 * <i><b>Combined formatting</b></i>
 * <ruby>漢<rt>kan</rt>字<rt>ji</rt></ruby>
 * ```
 *
 * ## Tag Conversion from SRT
 *
 * When converting SRT files:
 * - `<font>` tags are converted to `<c>` (color attributes are stripped)
 * - Standard tags (`<i>`, `<b>`, `<u>`) are preserved as-is
 * - Invalid HTML tags are removed for security
 *
 * @module subtitle-utils
 */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/**
 * Valid WebVTT formatting tags
 */
const VALID_VTT_TAGS = ["i", "b", "u", "c", "v", "lang", "ruby", "rt"];
/**
 * Sanitize subtitle text to allow only valid WebVTT tags
 * Preserves formatting tags while removing potentially harmful HTML
 */
export function sanitizeSubtitleText(text) {
    // Remove any script tags and their content
    let sanitized = text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ""); // replaceAll requires ES2021
    // Remove any style tags and their content
    sanitized = sanitized.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ""); // replaceAll requires ES2021
    // Remove any event handlers (onclick, onerror, etc.)
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, ""); // replaceAll requires ES2021
    // Remove any non-VTT tags except the valid ones
    const tagPattern = /<\/?([a-z]+)(?:\s+[^>]*)?>/gi; // NOSONAR
    sanitized = sanitized.replace(tagPattern, (match, tagName) => {
        // NOSONAR
        const tag = tagName.toLowerCase();
        // Keep valid VTT tags, remove everything else
        if (VALID_VTT_TAGS.includes(tag)) {
            return match;
        }
        return "";
    });
    return sanitized;
}
/**
 * Convert SRT HTML-like tags to WebVTT format
 * SRT often uses: <font color="...">text</font>
 * VTT uses: <c.classname>text</c>
 */
export function convertSrtTagsToVtt(text) {
    // Convert <font> tags to <c> tags (strip color attributes)
    const converted = text.replace(/<font[^>]*>/gi, "<c>").replace(/<\/font>/gi, "</c>"); // replaceAll requires ES2021
    // Preserve common formatting tags that work in both formats
    // (i, b, u are already compatible)
    return converted;
}
/**
 * Convert SRT timecode to VTT format
 * SRT: 00:00:20,000 --> 00:00:24,400
 * VTT: 00:00:20.000 --> 00:00:24.400
 */
export function srtTimeToVtt(srtTime) {
    return srtTime.split(",").join(".");
}
/**
 * Parse timecode string to milliseconds
 * Supports: HH:MM:SS.mmm (VTT), HH:MM:SS,mmm (SRT), HH:MM:SS:ff (theatrical @24fps)
 */
export function parseTimecode(timecode) {
    // Theatrical format (24fps): 01:00:00:00
    const theatricalRegex = /^(\d{1,2}):(\d{2}):(\d{2}):(\d{2})$/;
    const theatrical = theatricalRegex.exec(timecode);
    if (theatrical) {
        const [, hours, minutes, seconds, frames] = theatrical;
        const totalSeconds = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds) + Number(frames) / 24;
        return Math.round(totalSeconds * 1000);
    }
    // Standard format: HH:MM:SS.mmm or HH:MM:SS,mmm
    const standardRegex = /^(\d{1,2}):(\d{2}):(\d{2})[.,](\d{3})$/;
    const standard = standardRegex.exec(timecode);
    if (standard) {
        const [, hours, minutes, seconds, ms] = standard;
        return Number(hours) * 3600000 + Number(minutes) * 60000 + Number(seconds) * 1000 + Number(ms);
    }
    return 0;
}
/**
 * Format milliseconds to VTT timecode
 */
export function formatVttTimecode(ms) {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const milliseconds = ms % 1000;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(milliseconds).padStart(3, "0")}`;
}
/**
 * Convert SRT to VTT format
 * Preserves formatting tags and converts SRT-specific tags to VTT format
 */
export function srtToVtt(srtContent) {
    const lines = srtContent.split("\n");
    const vttLines = ["WEBVTT\n"];
    let inCue = false;
    for (const rawLine of lines) {
        const line = rawLine.trim();
        // Skip empty lines between cues
        if (!line && !inCue)
            continue;
        // Skip cue numbers (VTT doesn't use them)
        if (/^\d+$/.test(line)) {
            inCue = true;
            continue;
        }
        // Convert timecode line
        if (line.includes("-->")) {
            vttLines.push(srtTimeToVtt(line));
            continue;
        }
        // Subtitle text - convert tags and sanitize
        if (line) {
            const convertedText = convertSrtTagsToVtt(line);
            const sanitizedText = sanitizeSubtitleText(convertedText);
            vttLines.push(sanitizedText);
        }
        else if (inCue) {
            // End of cue
            vttLines.push("");
            inCue = false;
        }
    }
    return vttLines.join("\n");
}
/**
 * Normalize timecodes (currently disabled)
 * Always returns content unchanged with offset 0.
 * Timecodes should match the actual video timestamps.
 */
export function normalizeTimecodes(vttContent) {
    return { normalized: vttContent, offset: 0 };
}
/**
 * Add positioning cues to VTT timing lines
 * Adds horizontal centering and width metadata that CustomSubtitleOverlay respects.
 * Does NOT add vertical line positioning - that's handled dynamically by the player.
 */
export function addPositioning(vttContent, sizePercent = 60) {
    const lines = vttContent.split("\n");
    let modifiedCount = 0;
    const modifiedLines = lines.map((line) => {
        if (!line.includes("-->")) {
            return line;
        }
        // Remove existing positioning cues
        const cleanLine = line.split(/\s+line:|position:|size:|align:/)[0].trim(); // NOSONAR
        modifiedCount++;
        // Add horizontal centering and width (no vertical line position)
        return `${cleanLine} position:50% align:center size:${sizePercent}%`;
    });
    return { content: modifiedLines.join("\n"), modifiedCount };
}
/**
 * Validate VTT format with enhanced quality checks
 */
export function validateVtt(// NOSONAR
vttContent, options = {}) {
    // NOSONAR
    const errors = [];
    const warnings = [];
    // Apply defaults based on industry standards
    const maxLinesPerCue = options.maxLinesPerCue ?? 2;
    const maxCharsPerSecond = options.maxCharsPerSecond ?? 21;
    const maxLineLength = options.maxLineLength ?? 42;
    const minCueDuration = options.minCueDuration ?? 792; // 19 frames at 24fps (~833ms = 20 frames is Ooona minimum)
    const maxCueDuration = options.maxCueDuration ?? 7000; // 7 seconds
    const minGapBetweenCues = options.minGapBetweenCues ?? 83; // 2 frames at 24fps
    // Check for WEBVTT header
    if (!vttContent.trim().startsWith("WEBVTT")) {
        errors.push("Missing WEBVTT header");
    }
    // Check for at least one cue
    const cueCount = (vttContent.match(/-->/g) || []).length;
    if (cueCount === 0) {
        errors.push("No subtitle cues found");
    }
    // Validate timecode format and perform enhanced quality checks
    const lines = vttContent.split("\n");
    const timecodeRegex = /(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{3})/;
    let inCue = false;
    let currentCueStart = null;
    let currentCueEnd = null;
    let currentCueStartTimecode = null; // Track formatted timecode for messages
    let currentCueText = [];
    let previousCueEnd = null;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.includes("-->")) {
            const timecodeMatch = timecodeRegex.exec(line);
            if (timecodeMatch) {
                // Parse timecodes for enhanced validation
                currentCueStart = parseTimecode(timecodeMatch[1]);
                currentCueEnd = parseTimecode(timecodeMatch[2]);
                currentCueStartTimecode = timecodeMatch[1]; // Store formatted timecode for messages
                // Check for gap between cues (2 frames minimum)
                if (previousCueEnd !== null) {
                    const gap = currentCueStart - previousCueEnd;
                    if (gap < 0) {
                        warnings.push(`Cue at ${currentCueStartTimecode} overlaps with previous cue by ${Math.abs(gap)}ms`);
                    }
                    else if (gap < minGapBetweenCues) {
                        warnings.push(`Cue at ${currentCueStartTimecode} has only ${gap}ms gap (recommended minimum: ${minGapBetweenCues}ms / 2 frames)`);
                    }
                }
                // Check cue duration (ERRORS for violations)
                const cueDuration = currentCueEnd - currentCueStart;
                if (cueDuration < minCueDuration) {
                    errors.push(`Cue at ${currentCueStartTimecode} duration (${cueDuration}ms) is below minimum ${minCueDuration}ms`);
                }
                if (cueDuration > maxCueDuration) {
                    errors.push(`Cue at ${currentCueStartTimecode} duration (${cueDuration}ms) exceeds maximum ${maxCueDuration}ms`);
                }
                // Check if subtitle extends beyond video duration
                if (options.videoDuration !== undefined && currentCueEnd > options.videoDuration) {
                    warnings.push(`Cue at ${currentCueStartTimecode} ends beyond video duration (${formatVttTimecode(options.videoDuration)})`);
                }
            }
            else {
                errors.push(`Invalid timecode format at line ${i + 1}: ${line}`);
            }
            inCue = true;
            currentCueText = [];
        }
        else if (inCue && line.trim()) {
            // Store cue text for later analysis
            currentCueText.push(line.trim());
            // Check line length (ERROR if exceeds 42 chars)
            const lineLength = line.trim().length;
            if (lineLength > maxLineLength) {
                const timecodeRef = currentCueStartTimecode ? `Cue at ${currentCueStartTimecode}` : `Line ${i + 1}`;
                errors.push(`${timecodeRef}: line exceeds ${maxLineLength} chars (${lineLength})`);
            }
            // Check for potentially invalid tags in subtitle text
            const invalidTagPattern = /<(?!\/?(?:i|b|u|c|v|lang|ruby|rt)\b)[a-z]+[^>]*>/gi; // NOSONAR
            const invalidTags = line.match(invalidTagPattern);
            if (invalidTags) {
                const timecodeRef = currentCueStartTimecode ? `Cue at ${currentCueStartTimecode}` : `Line ${i + 1}`;
                warnings.push(`${timecodeRef}: non-standard tags ${invalidTags.join(", ")}`);
            }
            // Check for unclosed tags
            const openTagRegex = /<([a-z]+)[^>]*>/gi; // NOSONAR
            const closeTagRegex = /<\/([a-z]+)>/gi; // NOSONAR
            const openTags = [];
            const closeTags = [];
            let openMatch;
            while ((openMatch = openTagRegex.exec(line)) !== null) {
                const tagMatch = /<([a-z]+)/.exec(openMatch[0]);
                if (tagMatch?.[1])
                    openTags.push(tagMatch[1]);
            }
            let closeMatch;
            while ((closeMatch = closeTagRegex.exec(line)) !== null) {
                const tagMatch = /<\/([a-z]+)/.exec(closeMatch[0]);
                if (tagMatch?.[1])
                    closeTags.push(tagMatch[1]);
            }
            if (openTags.length !== closeTags.length) {
                const timecodeRef = currentCueStartTimecode ? `Cue at ${currentCueStartTimecode}` : `Line ${i + 1}`;
                warnings.push(`${timecodeRef}: may have unclosed formatting tags`);
            }
        }
        else if (inCue && !line.trim()) {
            // End of cue - perform comprehensive validation
            if (currentCueStart !== null && currentCueEnd !== null && currentCueText.length > 0) {
                // Check maximum lines per cue (ERROR if > 2)
                if (currentCueText.length > maxLinesPerCue) {
                    const timecodeRef = currentCueStartTimecode || "Unknown";
                    errors.push(`Cue at ${timecodeRef}: has ${currentCueText.length} lines (max ${maxLinesPerCue})`);
                }
                // Check reading speed (WARN if exceeds chars/sec)
                const fullText = currentCueText.join(" ");
                // Remove HTML tags for character count
                const textWithoutTags = fullText.replace(/<[^>]*>/g, ""); // replaceAll requires ES2021
                const charCount = textWithoutTags.length;
                const durationSeconds = (currentCueEnd - currentCueStart) / 1000;
                if (durationSeconds > 0 && charCount > 0) {
                    const charsPerSecond = charCount / durationSeconds;
                    if (charsPerSecond > maxCharsPerSecond) {
                        const timecodeRef = currentCueStartTimecode || "Unknown";
                        warnings.push(`Cue at ${timecodeRef}: reading speed ${charsPerSecond.toFixed(1)} chars/sec exceeds ${maxCharsPerSecond} (too fast)`);
                    }
                }
                // Update previous cue end for gap detection
                previousCueEnd = currentCueEnd;
            }
            inCue = false;
            currentCueStart = null;
            currentCueEnd = null;
            currentCueStartTimecode = null;
            currentCueText = [];
        }
    }
    // Warn if file is large
    const sizeBytes = new Blob([vttContent]).size;
    if (sizeBytes > 5 * 1024 * 1024) {
        warnings.push(`File size (${(sizeBytes / 1024 / 1024).toFixed(2)}MB) exceeds 5MB`);
    }
    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}
/**
 * Calculate validation metrics from error/warning messages
 */
export function calculateValidationMetrics(validation) {
    const readingSpeedIssues = validation.warnings.filter((w) => w.includes("reading speed") || w.includes("chars/sec")).length;
    const gapIssues = validation.warnings.filter((w) => w.includes("gap") || w.includes("overlap")).length;
    const durationIssues = validation.errors.filter((e) => e.includes("duration") || e.includes("second")).length;
    const maxLinesIssues = validation.errors.filter((e) => e.includes("lines") && e.includes("maximum"));
    const maxLineLengthIssues = validation.errors.filter((e) => e.includes("exceeds") && e.includes("chars"));
    return {
        maxLinesPerCue: maxLinesIssues.length > 0 ? 3 : 2,
        maxLineLength: maxLineLengthIssues.length > 0
            ? Math.max(42, ...maxLineLengthIssues.map((e) => {
                const m = /(\d+) chars/.exec(e); // NOSONAR
                return m ? Number.parseInt(m[1], 10) : 42;
            }))
            : 42,
        readingSpeedIssues,
        gapIssues,
        durationIssues,
    };
}
/**
 * Process subtitle file (complete pipeline)
 */
export function processSubtitleFile(fileContent, fileName, options = {}) {
    const isSrt = fileName.toLowerCase().endsWith(".srt");
    // Step 1: Convert SRT → VTT if needed
    let vttContent = isSrt ? srtToVtt(fileContent) : fileContent;
    const wasConverted = isSrt;
    // Step 2: Normalize timecodes
    const { normalized, offset } = normalizeTimecodes(vttContent);
    vttContent = normalized;
    const wasNormalized = offset > 0;
    // Step 3: Add positioning cues (horizontal centering and width only)
    const { content: positionedContent, modifiedCount } = addPositioning(vttContent, options.sizePercent ?? 60);
    vttContent = positionedContent;
    // Step 4: Validate with enhanced options
    const validationResult = validateVtt(vttContent, options.validation);
    const metrics = calculateValidationMetrics(validationResult);
    // Calculate stats - count cues regardless of validation errors
    const cueCount = (vttContent.match(/\d{2}:\d{2}:\d{2}\.\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}\.\d{3}/g) || []).length;
    const sizeKB = new Blob([vttContent]).size / 1024;
    return {
        vttContent,
        wasConverted,
        wasNormalized,
        timecodeOffset: offset,
        modifiedCueCount: modifiedCount,
        cueCount,
        sizeKB,
        validation: {
            ...validationResult,
            metrics,
        },
    };
}
//# sourceMappingURL=subtitle-utils.js.map